import { useMemo } from "react";
import { ITempProfileData } from "../../../../redux/profile/tempProfiles/slice";
import { IProfile } from "./useProfileData";
import { validateChildBirthDate } from "../../../../helpers/dateFormatter";

const validateEmail = (email: string): boolean => {
  if (!email || !email.trim()) {
    return false;
  }
  return email.trim().match(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/) !== null;
};

const validatePhone = (phone: string): boolean => {
  if (!phone || !phone.trim()) {
    return false;
  }
  const trimmedPhone = phone.trim();
  const phoneRegex = /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/;
  return trimmedPhone.length === 18 && phoneRegex.test(trimmedPhone);
};

const validateProfile = (profileData: ITempProfileData, profileType: string): boolean => {
  if (!profileData || !profileData.name || profileData.name.trim() === "") {
    return false;
  }

  if (profileType === "child") {
    const birthDateValidation = validateChildBirthDate(profileData.birthDate);
    return birthDateValidation.isValid;
  } else {
    return validatePhone(profileData.phone || "");
  }
};

const validateGiftProfile = (profileData: ITempProfileData, isReceiver: boolean = false): boolean => {
  if (!profileData || !profileData.name || profileData.name.trim() === "") {
    return false;
  }

  if (!validatePhone(profileData.phone || "")) {
    return false;
  }

  if (isReceiver) {
    if (!profileData.email || !validateEmail(profileData.email)) {
      return false;
    }

    if (!profileData.message || profileData.message.trim() === "") {
      return false;
    }
  }

  return true;
};

export const useProfileValidation = (
  tempProfilesData: { [profileId: string]: ITempProfileData },
  allProfiles: IProfile[],
  hasGift: boolean,
  deliveryDate: string
) => {
  const validateProfileFunc = (profileData: ITempProfileData, profileType: string): boolean => {
    return validateProfile(profileData, profileType);
  };

  const validateAllProfiles = useMemo((): boolean => {
    if (hasGift) {
      const senderData = tempProfilesData["sender"] || {};
      const receiverData = tempProfilesData["receiver"] || {};
      const senderValid = validateGiftProfile(senderData, false);
      const receiverValid = validateGiftProfile(receiverData, true);
      const deliveryDateValid = !!(deliveryDate && deliveryDate.trim() !== "");
      
      return senderValid && receiverValid && deliveryDateValid;
    }

    if (allProfiles.length === 0) {
      return false;
    }

    const validationResults = allProfiles.map((profile) => {
      const profileData = tempProfilesData[profile.id] || {};
      const isValid = validateProfile(profileData, profile.type);
      
      return isValid;
    });

    const allValid = validationResults.every((isValid) => isValid);

    return allValid;
  }, [tempProfilesData, allProfiles, hasGift, deliveryDate]);

  const validateGiftProfileFunc = (profileData: ITempProfileData, isReceiver: boolean): boolean => {
    return validateGiftProfile(profileData, isReceiver);
  };

  return {
    validateProfile: validateProfileFunc,
    validateAllProfiles,
    validateGiftProfile: validateGiftProfileFunc,
  };
};

